<template>
  <div class="BaseAutocompleteInput">
    <label
      :id="id + '-label'"
      :for="id"
      >{{ label }}</label
    >

    <v-combobox
      :id="id"
      ref="$field"
      :aria-labelledby="id + '-label'"
      :title="label"
      :aria-describedby="fieldValidationErrorMessage ? id + '-error' : undefined"
      :aria-invalid="!!fieldValidationErrorMessage"
      :placeholder="placeholder"
      :items="items"
      :modelValue="value"
      @update:modelValue="value = parseValue($event)"
      menu-icon=""
      auto-select-first
      :rules="rules"
      :hide-details="true"
      :hide-no-data="true"
      :loading="loading"
      :disabled="!editable"
      :maxlength="maxlength"
      :menu-props="{ class: 'menu-autocomplete' }"
      @update:search="emit('update', parseValue($event))"
    />

    <ErrorMessageComponent
      :messages="fieldValidationErrorMessage"
      :id="id + '-error'"
    />
  </div>
</template>

<script setup lang="ts">
  import { useValidationError } from '@/modules/core/composables/validationErrorsComposable'
  import type { ValidationRuleType } from '@/modules/core/models/types'
  import { nextTick, onBeforeUnmount, onMounted } from 'vue'
  import {
    endDuplicatedLabelControl,
    startDuplicatedLabelControl
  } from '../../composables/accesibilityComposable'
  import ErrorMessageComponent from '../formFields/ErrorMessageComponent.vue'

  interface PropsInterface {
    id: string
    modelValue: string
    label?: string
    items?: string[]
    placeholder?: string
    rules?: ValidationRuleType[]
    loading?: boolean
    editable?: boolean
    maxlength?: number
  }

  withDefaults(defineProps<PropsInterface>(), {
    items: () => [],
    rules: () => [],
    loading: false,
    editable: true
  })

  const emit = defineEmits(['update'])

  const value = defineModel({ type: String })

  function parseValue(value: string | null) {
    if (!value) return ''

    return String(value)
      .trim()
      .normalize('NFD')
      .replace(/([aeiouAEIOU])[\u0300-\u036f]/g, '$1')
  }

  const { $field, fieldValidationErrorMessage } = useValidationError()

  let observer: MutationObserver | null = null

  onMounted(async () => {
    await nextTick()
    const rootElement = ($field.value as any)?.$el as HTMLElement | null
    observer = startDuplicatedLabelControl(rootElement)
  })

  onBeforeUnmount(() => {
    endDuplicatedLabelControl(observer)
  })
</script>

<style lang="scss" scoped>
  .BaseAutocompleteInput {
    display: flex;
    flex-direction: column;
    gap: $gap-s;

    label {
      font-family: $font-family-primary;
      font-size: $font-size-m;
      font-style: normal;
      font-weight: $font-weight-400;
      line-height: $line-height-100p;
      color: $color-neutral-dark;
    }

    :deep(.v-field) {
      background-color: $color-grey-200;
      border-radius: $b-radius-0;
      box-shadow: $box-shadow-0;
      transition: all 0.2s ease-in-out;

      &:focus-within {
        box-shadow: $box-shadow-focus;
      }

      .v-field__input input {
        &:focus,
        &:focus-visible {
          outline: none;
          box-shadow: $box-shadow-none;
        }
      }

      .v-field__prepend-inner {
        i::before {
          font-family: $font-family-icons;
        }
      }

      .v-field__input {
        font-family: $font-family-primary;
        color: $color-neutral-dark;

        &::placeholder {
          color: $color-neutral;
          opacity: 1;
        }
      }

      &.v-field--disabled {
        opacity: 1;

        .v-combobox__selection-text {
          color: $color-neutral;
        }

        .v-field__input {
          input{
            border:none;
          }
        }
      }

      &.v-field--error {
        background-color: $color-error-200;

        .v-field__input {
          color: $color-error;

          &::placeholder {
            color: $color-neutral;
            opacity: 1;
          }
        }
      }
    }
  }
</style>
